'use client';
import React, { useState, Suspense } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] gradient-primary flex-col justify-between p-10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="text-white font-800 text-xl">ContactSense</span>
        </div>
        <div>
          <h1 className="text-hero-xl font-800 text-white leading-tight mb-6">
            Find the right business contact in seconds.
          </h1>
          <p className="text-white/80 text-base mb-10 leading-relaxed">
            Discover verified, publicly available company contacts by industry, role, location, and purpose.
          </p>
          <div className="flex flex-col gap-4">
            {[
              { icon: 'SparklesIcon', title: 'AI-powered natural language search', desc: 'Describe what you need — AI structures the query' },
              { icon: 'ShieldCheckIcon', title: 'Verified public business emails', desc: 'Source-backed contacts with confidence scores' },
              { icon: 'RectangleStackIcon', title: 'Bulk discovery up to 500 results', desc: 'Run large searches with live progress tracking' },
              { icon: 'ArrowDownTrayIcon', title: 'Export to CSV, XLSX, or JSON', desc: 'CRM-ready exports with full source metadata' },
            ].map((f) => (
              <div key={`feature-${f.title}`} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-700">{f.title}</p>
                  <p className="text-white/60 text-xs mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/40 text-xs">
          ContactSense AI — Business contacts sourced from public, legitimate sources only.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-800 text-xl text-foreground">ContactSense</span>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-xl border border-border bg-muted p-1 mb-8">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 rounded-lg text-sm font-600 transition-all duration-150
                  ${tab === t ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Wrap in Suspense because LoginForm uses useSearchParams */}
          <Suspense fallback={<div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>}>
            {tab === 'login' ? <LoginForm /> : <SignupForm onSwitchToLogin={() => setTab('login')} />}
          </Suspense>
        </div>
      </div>
    </div>
  );
}